import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CatalogTargetOnWire, CatalogVersionView, CategoryStatusView, MarketCatalogView, ResolvedCatalogCategoryView } from '@vello/api-client';
import { ICON_NAMES } from '@vello/icons';
import { CATALOG_REASON_MAX } from '@vello/service-categories';
import {
  maySelfApprove,
  cashHandoverActor,
  CATALOG_EDITOR_LANGUAGES,
  CATALOG_FEATURE_NAMES,
  CATALOG_VEHICLE_CLASSES,
  catalogApproveBlock,
  catalogEntryForm,
  catalogEntryFromForm,
  catalogRejectable,
  catalogWithdrawable,
  copyResultSummary,
  editorTargets,
  formatMarketInstant,
  latestApproved,
  loadMarketCatalog,
  marketTimeZone,
  pendingCatalogVersions,
  pendingReactivations,
  serviceCategoryAuthority,
  targetFromKey,
  targetKey,
  toggle,
  type CatalogEntryForm,
} from '../../src/index';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { usePersonNames } from '../components/person-name';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { usePlaceNames } from '../i18n/scope-name';
import { Badge, Button, CheckRow, Field, Notice, PageHeader, Panel, Select, Table, TextInput, FormGrid, FormRow, SectionTitle, Cluster, Divider, EmptyState, CapWidth, NoteText, Stack, Text } from '@vello/web-theme';

type Msg = { readonly tone: 'success' | 'danger' | 'warning'; readonly text: string };


/**
 * **Tarifas › Catálogo de categorias** (W9-CATEGORY-CATALOG, D462, ADR-0080; DevMobility B-001, A-011, B-002, B-004, B-010,
 * B-018, B-021, B-049, I-100) — the governed service-category catalog over `/v1/config/service-categories/*`.
 *
 * - The **market** (chosen from the operator's reach) and what it resolves NOW: each category with the scope and version
 *   that decided it (market, its administrative region, its country or the platform), its status, its features, and the
 *   fare that would price it (the market's or the region's) — a category with no fare is never offered.
 * - **Pending** versions and reactivations of the market's buckets: Approve is HIDDEN from its author with the reason
 *   shown; Withdraw is the author's; every decision needs a reason. The server compares the session identities anyway.
 * - **Switch off** a category at a bucket at once (no new version); ask a second operator to switch it back on.
 * - The **editor**: an entry as a form for one bucket, validated by the kernel before sending; the screen's one primary
 *   action proposes it. **Copy** it (and its fare) to other markets as pending drafts.
 * A refused read is an error, never an empty catalog. No native dialog is ever opened.
 */
export function ServiceCategoriesScreen(): ReactElement {
  const { runtime, markets, me, grants } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { t, lang } = useT();
  const { v: word, fail, explain, reason } = useVocab();
  const place = usePlaceNames();
  const operatorId = me?.id;
  const { mayPropose, mayApprove } = serviceCategoryAuthority(grants);
  // The catalog is the header scope's market's (owner, 2026-10-12) — no second market selector on the screen.
  const { market, choices } = useScopeMarket();
  const marketId = market ?? '';
  const [view, setView] = useState<MarketCatalogView | undefined>(undefined);
  const [versions, setVersions] = useState<readonly CatalogVersionView[]>([]);
  const [status, setStatus] = useState<readonly CategoryStatusView[]>([]);
  const [error, setError] = useState<string | undefined>(undefined);
  const [msg, setMsg] = useState<Msg | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [decisionReason, setDecisionReason] = useState('');
  const [switchCode, setSwitchCode] = useState('');
  const [switchTarget, setSwitchTarget] = useState('');
  const [editorTarget, setEditorTarget] = useState('');
  const [form, setForm] = useState<CatalogEntryForm>(() => catalogEntryForm());
  const [proposalReason, setProposalReason] = useState('');
  const [copyTargets, setCopyTargets] = useState<readonly string[]>([]);
  const [copyFare, setCopyFare] = useState(true);
  const [copyReason, setCopyReason] = useState('');
  const seq = useRef(0);
  const tz = marketTimeZone(marketId, markets);

  const when = (instant: string | undefined): string => formatMarketInstant(instant, tz, lang);
  const who = (id: string | undefined): string => {
    const actor = cashHandoverActor(id ?? null, undefined, operatorId);
    return actor === undefined ? '—' : actor.kind === 'you' ? t('svccat.actor.you') : (personName(id) ?? actor.text);
  };
  const refusal = (e: { reasonCode?: string; code: string }): string => {
    return explain(e) ?? t('svccat.refusal.other', { code: fail(e) });
  };
  /** Where a catalog change applies, in words: the platform, a country, a region or a city — never its id. */
  const bucket = (tg: CatalogTargetOnWire): string =>
    tg.scope === 'country' ? place.country(tg.scopeRef) : tg.scope === 'market' ? place.market(tg.scopeRef) : word('policy.scope', tg.scope);
  const labelOf = (c: { labels: Readonly<Record<string, string>>; code: string }): string => c.labels[lang] ?? c.labels[Object.keys(c.labels).sort()[0] ?? ''] ?? word('ride.category', c.code);
  /** The name of a category by its id: the catalog's own labels when it is listed, else the platform's default name. */
  const categoryName = (code: string): string => {
    const listed = view?.categories.find((c) => c.code === code);
    return listed !== undefined ? labelOf(listed) : word('ride.category', code);
  };

  const targets = useMemo(() => editorTargets(view?.market), [view]);

  const load = useCallback(
    async (m: string): Promise<void> => {
      const n = ++seq.current;
      setError(undefined);
      setView(undefined);
      if (m === '') return;
      const res = await loadMarketCatalog(runtime, m);
      if (n !== seq.current) return;
      if (!res.ok) {
        setError(t('svccat.error.load', { code: fail(res.error) }));
        return;
      }
      setView(res.value);
      // The changes of every bucket this market inherits from that the operator may read (a refused bucket is skipped).
      const lists = await Promise.all(editorTargets(res.value.market).map((tg) => runtime.serviceCategories.list({ scope: tg.scope, scopeRef: tg.scopeRef })));
      if (n !== seq.current) return;
      setVersions(lists.flatMap((l) => (l.ok ? l.value.versions : [])));
      setStatus(lists.flatMap((l) => (l.ok ? l.value.status : [])));
    },
    [runtime, t],
  );

  useEffect(() => {
    void load(marketId);
  }, [marketId, load]);
  useEffect(() => {
    if (targets.length > 0 && editorTarget === '') setEditorTarget(targetKey(targets[0]!));
    if (targets.length > 0 && switchTarget === '') setSwitchTarget(targetKey(targets[0]!));
  }, [targets, editorTarget, switchTarget]);

  const act = async (run: () => Promise<{ ok: boolean; error?: { reasonCode?: string; code: string } }>, success: string): Promise<void> => {
    setBusy(true);
    setMsg(undefined);
    const res = await run();
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: refusal(res.error ?? { code: 'INTERNAL' }) });
      return;
    }
    setMsg({ tone: 'success', text: success });
    setDecisionReason('');
    await load(marketId);
  };

  const target = targetFromKey(editorTarget);
  const draft = target !== undefined ? catalogEntryFromForm(form, target) : undefined;
  const base = target !== undefined && form.code.trim() !== '' ? latestApproved(versions, target, form.code.trim()) : undefined;
  const hasDecisionReason = decisionReason.trim() !== '';

  const edit = (c: ResolvedCatalogCategoryView): void => {
    const v = versions.find((x) => x.scope === c.source.scope && x.scopeRef === c.source.scopeRef && x.code === c.code && x.version === c.source.version);
    setEditorTarget(targetKey(c.source));
    setForm(catalogEntryForm(v?.entry));
    setMsg(undefined);
  };

  const propose = async (): Promise<void> => {
    if (target === undefined || draft === undefined || !draft.ok) return;
    await act(async () => {
      const res = await runtime.serviceCategories.propose({ ...target, entry: draft.value, ...(base !== undefined ? { baseVersion: base.version } : {}), reason: proposalReason.trim() });
      return res.ok ? { ok: true } : { ok: false, error: res.error };
    }, t('svccat.editor.proposed'));
    setProposalReason('');
  };

  const pending = pendingCatalogVersions(versions);
  const reactivations = pendingReactivations(status);
  const inactive = status.filter((s) => !s.active && s.pendingReactivation === undefined);
  const personName = usePersonNames([...pending.map((x) => x.proposedBy), ...reactivations.map((x) => x.pendingReactivation?.requestedBy), ...inactive.map((x) => x.changedBy)]);
  const copyChoices = choices.filter((c) => c.id !== marketId);

  const categoryColumns = [
    { key: 'order', header: t('svccat.col.order'), numeric: true, width: '64px', render: (c: ResolvedCatalogCategoryView) => <span className="tnum">{c.order}</span> },
    {
      key: 'name',
      header: t('svccat.col.name'),
      width: '2fr',
      render: (c: ResolvedCatalogCategoryView) => (
        <span>
          <strong>{labelOf(c)}</strong>
          <Text role="caption" tone="tertiary" block>{`${word('ui.icon_token', c.iconToken)} · ${word('service_categories.aggregate_kind', c.kind)}`}</Text>
        </span>
      ),
    },
    {
      key: 'vehicles',
      header: t('svccat.col.vehicles'),
      render: (c: ResolvedCatalogCategoryView) => (
        <Cluster gap={2} wrap>
          {c.vehicleClasses.map((vc) => <Badge key={vc}>{word('service_categories.vehicle_class', vc)}</Badge>)}
          {!c.motorized && <Badge tone="warning">{t('svccat.vehicle.nonMotorized')}</Badge>}
        </Cluster>
      ),
    },
    {
      key: 'features',
      header: t('svccat.col.features'),
      width: '2fr',
      render: (c: ResolvedCatalogCategoryView) => {
        const on = Object.entries(c.features).filter(([, v]) => v).map(([k]) => k);
        return on.length === 0 ? <Text role="caption" tone="tertiary" block>{t('svccat.features.none')}</Text> : <Cluster gap={2} wrap>{on.map((f) => <Badge key={f}>{word('service_categories.category_feature', f)}</Badge>)}</Cluster>;
      },
    },
    { key: 'source', header: t('svccat.col.source'), render: (c: ResolvedCatalogCategoryView) => <span className="tnum">{`${bucket(c.source)} · v${c.source.version}`}</span> },
    {
      key: 'fare',
      header: t('svccat.col.fare'),
      render: (c: ResolvedCatalogCategoryView) => (c.fare === null ? <Badge tone="warning">{t('svccat.fare.none')}</Badge> : <Text role="caption" tone="tertiary" block>{`${word('policy.scope', c.fare.scope)} · ${c.fare.pricingVersion}`}</Text>),
    },
    {
      key: 'status',
      header: t('svccat.col.status'),
      render: (c: ResolvedCatalogCategoryView) =>
        c.status === 'ACTIVE' ? <Badge tone="success">{t('svccat.status.active')}</Badge> : <Badge tone="danger">{t('svccat.status.inactiveAt', { where: c.inactiveAt !== undefined ? bucket(c.inactiveAt) : '—' })}</Badge>,
    },
    ...(mayPropose
      ? [{ key: 'actions', header: '', render: (c: ResolvedCatalogCategoryView) => <Button size="sm" variant="secondary" onClick={() => edit(c)}>{t('svccat.action.edit')}</Button> }]
      : []),
  ];

  const setLabel = (field: 'labels' | 'message', language: string, value: string): void => setForm({ ...form, [field]: { ...form[field], [language]: value } });

  return (
    <>
      <PageHeader title={t('svccat.title')} subtitle={t('svccat.subtitle')} />

      <Panel>
        <Stack gap={4}>
        <Text as="p" tone="secondary" block>{t('svccat.explain')}</Text>
        {market === undefined && <ScopeMarketNotice choices={choices} />}
        {error !== undefined && <Notice tone="danger">{error}</Notice>}
        </Stack>
      </Panel>

      {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}

      {view !== undefined && (
        <>
          <section aria-label={t('svccat.resolved.title')}>
            <Panel>
              <Stack gap={3}>
              <SectionTitle>{t('svccat.resolved.title')}</SectionTitle>
              <Text as="p" tone="secondary" block>{t('svccat.resolved.explain', { ancestry: view.ancestry.map(bucket).join(' → ') })}</Text>
              <Table
                columns={categoryColumns}
                rows={view.categories}
                rowKey={(c) => c.code}
                empty={<EmptyState title={t('svccat.resolved.empty')} />}
              />
              </Stack>
            </Panel>
          </section>

          <section aria-label={t('svccat.pending.title')}>
            <Panel>
              <Stack gap={3}>
              <SectionTitle>{t('svccat.pending.title')}</SectionTitle>
              {pending.length === 0 && reactivations.length === 0 ? (
                <Text as="p" tone="secondary" block>{t('svccat.pending.none')}</Text>
              ) : (
                <>
                  <CapWidth size="lg">
                    <Field label={t('svccat.decision.reason')} htmlFor="svccat-decision-reason" hint={t('svccat.decision.reason.hint')}>
                      <TextInput id="svccat-decision-reason" value={decisionReason} maxLength={CATALOG_REASON_MAX} onChange={(e) => setDecisionReason(e.target.value)} />
                    </Field>
                  </CapWidth>
                  {pending.map((v) => {
                    const block = catalogApproveBlock(v.proposedBy, v.state === 'PENDING', operatorId, mayApprove, selfApproval);
                    const decide = (action: 'APPROVE' | 'REJECT' | 'WITHDRAW') =>
                      void act(async () => {
                        const res = await runtime.serviceCategories.decide({ scope: v.scope, scopeRef: v.scopeRef, code: v.code, version: v.version, action, reason: decisionReason.trim() });
                        return res.ok ? { ok: true } : { ok: false, error: res.error };
                      }, t(`svccat.decision.done.${action}`));
                    return (
                      <Stack key={`${v.scope}|${v.scopeRef}|${v.code}|${v.version}`} gap={2}>
                        <Divider />
                        <Text as="p">
                          <strong>{labelOf({ labels: v.entry.labels, code: v.code })}</strong>{' '}
                          <span>{t('svccat.pending.meta', { bucket: bucket(v), version: String(v.version), by: who(v.proposedBy), at: when(v.proposedAt) })}</span>
                        </Text>
                        <Text as="p" role="caption" tone="tertiary">{t('svccat.pending.why', { reason: v.reason })}</Text>
                        {v.copiedFrom !== undefined && <Text as="p" role="caption" tone="tertiary">{t('svccat.pending.copiedFrom', { bucket: bucket(v.copiedFrom), version: String(v.copiedFrom.version) })}</Text>}
                        <Cluster gap={2} wrap>
                          {block === undefined && <Button variant="secondary" disabled={busy || !hasDecisionReason} onClick={() => decide('APPROVE')}>{t('svccat.decision.approve')}</Button>}
                          {catalogRejectable(v.proposedBy, true, operatorId, mayApprove) && <Button variant="danger" disabled={busy || !hasDecisionReason} onClick={() => decide('REJECT')}>{t('svccat.decision.reject')}</Button>}
                          {catalogWithdrawable(v.proposedBy, true, operatorId, mayPropose) && <Button variant="danger" disabled={busy || !hasDecisionReason} onClick={() => decide('WITHDRAW')}>{t('svccat.decision.withdraw')}</Button>}
                        </Cluster>
                        {block !== undefined && block !== 'NOT_PENDING' && <NoteText>{word('service_categories.decision_block', block)}</NoteText>}
                      </Stack>
                    );
                  })}
                  {reactivations.map((s) => {
                    const requester = s.pendingReactivation!.requestedBy;
                    const block = catalogApproveBlock(requester, true, operatorId, mayApprove, selfApproval);
                    const decide = (action: 'APPROVE' | 'REJECT' | 'WITHDRAW') =>
                      void act(async () => {
                        const res = await runtime.serviceCategories.decideReactivation({ scope: s.scope, scopeRef: s.scopeRef, code: s.code, action, reason: decisionReason.trim() });
                        return res.ok ? { ok: true } : { ok: false, error: res.error };
                      }, t(`svccat.reactivation.done.${action}`));
                    return (
                      <Stack key={`r|${s.scope}|${s.scopeRef}|${s.code}`} gap={2}>
                        <Divider />
                        <Text as="p">
                          <strong>{t('svccat.reactivation.title', { code: categoryName(s.code), bucket: bucket(s) })}</strong>{' '}
                          <span>{t('svccat.reactivation.meta', { by: who(requester), at: when(s.pendingReactivation!.requestedAt) })}</span>
                        </Text>
                        <Text as="p" role="caption" tone="tertiary">{t('svccat.pending.why', { reason: s.pendingReactivation!.reason })}</Text>
                        <Cluster gap={2} wrap>
                          {block === undefined && <Button variant="secondary" disabled={busy || !hasDecisionReason} onClick={() => decide('APPROVE')}>{t('svccat.decision.approve')}</Button>}
                          {catalogRejectable(requester, true, operatorId, mayApprove) && <Button variant="danger" disabled={busy || !hasDecisionReason} onClick={() => decide('REJECT')}>{t('svccat.decision.reject')}</Button>}
                          {catalogWithdrawable(requester, true, operatorId, mayPropose) && <Button variant="danger" disabled={busy || !hasDecisionReason} onClick={() => decide('WITHDRAW')}>{t('svccat.decision.withdraw')}</Button>}
                        </Cluster>
                        {block !== undefined && block !== 'NOT_PENDING' && <NoteText>{word('service_categories.decision_block', block)}</NoteText>}
                      </Stack>
                    );
                  })}
                </>
              )}
              </Stack>
            </Panel>
          </section>

          <section aria-label={t('svccat.switch.title')}>
            <Panel>
              <Stack gap={3}>
              <SectionTitle>{t('svccat.switch.title')}</SectionTitle>
              <Text as="p" tone="secondary" block>{t('svccat.switch.explain')}</Text>
              {inactive.length > 0 && (
                <>
                  <SectionTitle level={3} size="sub">{t('svccat.switch.inactive')}</SectionTitle>
                  {inactive.map((s) => (
                    <Cluster key={`i|${s.scope}|${s.scopeRef}|${s.code}`} gap={2} wrap>
                      <span>{t('svccat.switch.inactiveRow', { code: categoryName(s.code), bucket: bucket(s), by: who(s.changedBy), at: when(s.changedAt), reason: s.reason })}</span>
                      {mayPropose && (
                        <Button size="sm" variant="secondary" disabled={busy || !hasDecisionReason} onClick={() => void act(async () => {
                          const res = await runtime.serviceCategories.requestReactivation({ scope: s.scope, scopeRef: s.scopeRef, code: s.code, reason: decisionReason.trim() });
                          return res.ok ? { ok: true } : { ok: false, error: res.error };
                        }, t('svccat.reactivation.requested'))}>{t('svccat.action.requestReactivation')}</Button>
                      )}
                    </Cluster>
                  ))}
                  <Text as="p" role="caption" tone="tertiary" block>{t('svccat.switch.reasonHint')}</Text>
                </>
              )}
              {mayPropose ? (
                <FormRow
                  action={
                    <Button variant="danger" disabled={busy || switchCode === '' || !hasDecisionReason || targetFromKey(switchTarget) === undefined} onClick={() => {
                      const tg = targetFromKey(switchTarget);
                      if (tg === undefined) return;
                      void act(async () => {
                        const res = await runtime.serviceCategories.deactivate({ ...tg, code: switchCode, reason: decisionReason.trim() });
                        return res.ok ? { ok: true } : { ok: false, error: res.error };
                      }, t('svccat.switch.done'));
                    }}>{t('svccat.action.deactivate')}</Button>
                  }
                >
                  <Field label={t('svccat.switch.code')} htmlFor="svccat-switch-code">
                    <TextInput id="svccat-switch-code" value={switchCode} onChange={(e) => setSwitchCode(e.target.value.trim())} />
                  </Field>
                  <Field label={t('svccat.switch.where')} htmlFor="svccat-switch-where">
                    <Select id="svccat-switch-where" value={switchTarget} onChange={(e) => setSwitchTarget(e.target.value)}>
                      {targets.map((tg) => <option key={targetKey(tg)} value={targetKey(tg)}>{bucket(tg)}</option>)}
                    </Select>
                  </Field>
                  <Field label={t('svccat.switch.reason')} htmlFor="svccat-switch-reason">
                    <TextInput id="svccat-switch-reason" value={decisionReason} maxLength={CATALOG_REASON_MAX} onChange={(e) => setDecisionReason(e.target.value)} />
                  </Field>
                </FormRow>
              ) : (
                <Notice tone="neutral">{t('svccat.noPermission')}</Notice>
              )}
              </Stack>
            </Panel>
          </section>

          <section aria-label={t('svccat.editor.title')}>
            <Panel>
              <Stack gap={3}>
              <SectionTitle>{t('svccat.editor.title')}</SectionTitle>
              <Text as="p" tone="secondary" block>{t('svccat.editor.explain')}</Text>
              {!mayPropose ? (
                <Notice tone="neutral">{t('svccat.noPermission')}</Notice>
              ) : (
                <>
                  <FormGrid>
                    <Field label={t('svccat.editor.where')} htmlFor="svccat-editor-where" hint={t('svccat.editor.where.hint')}>
                      <Select id="svccat-editor-where" value={editorTarget} onChange={(e) => setEditorTarget(e.target.value)}>
                        {targets.map((tg) => <option key={targetKey(tg)} value={targetKey(tg)}>{bucket(tg)}</option>)}
                      </Select>
                    </Field>
                    <Field label={t('svccat.editor.code')} htmlFor="svccat-code" hint={t('svccat.editor.code.hint')}>
                      <TextInput id="svccat-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.trim() })} />
                    </Field>
                    <Field label={t('svccat.editor.kind')} htmlFor="svccat-kind">
                      <Select id="svccat-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value === 'delivery' ? 'delivery' : 'mobility' })}>
                        <option value="mobility">{word('service_categories.aggregate_kind', 'mobility')}</option>
                        <option value="delivery">{word('service_categories.aggregate_kind', 'delivery')}</option>
                      </Select>
                    </Field>
                    <Field label={t('svccat.editor.icon')} htmlFor="svccat-icon">
                      <Select id="svccat-icon" value={form.iconToken} onChange={(e) => setForm({ ...form, iconToken: e.target.value })}>
                        {ICON_NAMES.map((i) => <option key={i} value={i}>{word('ui.icon_token', i)}</option>)}
                      </Select>
                    </Field>
                    <Field label={t('svccat.editor.order')} htmlFor="svccat-order" hint={t('svccat.editor.order.hint')}>
                      <TextInput id="svccat-order" inputMode="numeric" className="tnum" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
                    </Field>
                    <Field label={t('svccat.editor.seats')} htmlFor="svccat-seats" hint={t('svccat.editor.seats.hint')}>
                      <TextInput id="svccat-seats" inputMode="numeric" className="tnum" value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} />
                    </Field>
                    <Field label={t('svccat.editor.nameKey')} htmlFor="svccat-namekey" hint={t('svccat.editor.nameKey.hint')}>
                      <TextInput id="svccat-namekey" value={form.nameKey} onChange={(e) => setForm({ ...form, nameKey: e.target.value })} />
                    </Field>
                  </FormGrid>
                  <SectionTitle level={3} size="sub">{t('svccat.editor.labels')}</SectionTitle>
                  <FormGrid>
                    {CATALOG_EDITOR_LANGUAGES.map((l) => (
                      <Field key={`label-${l}`} label={t('svccat.editor.labelIn', { language: l })} htmlFor={`svccat-label-${l}`}>
                        <TextInput id={`svccat-label-${l}`} value={form.labels[l] ?? ''} maxLength={60} onChange={(e) => setLabel('labels', l, e.target.value)} />
                      </Field>
                    ))}
                  </FormGrid>
                  <SectionTitle level={3} size="sub">{t('svccat.editor.message')}</SectionTitle>
                  <FormGrid>
                    {CATALOG_EDITOR_LANGUAGES.map((l) => (
                      <Field key={`msg-${l}`} label={t('svccat.editor.labelIn', { language: l })} htmlFor={`svccat-msg-${l}`}>
                        <TextInput id={`svccat-msg-${l}`} value={form.message[l] ?? ''} maxLength={280} onChange={(e) => setLabel('message', l, e.target.value)} />
                      </Field>
                    ))}
                  </FormGrid>
                  <SectionTitle level={3} size="sub">{t('svccat.editor.vehicles')}</SectionTitle>
                  <Cluster gap={2} wrap>
                    {CATALOG_VEHICLE_CLASSES.map((v) => (
                      <CheckRow key={v} id={`svccat-vehicle-${v}`} checked={form.vehicleClasses.includes(v)} onChange={() => setForm({ ...form, vehicleClasses: toggle(form.vehicleClasses, v) })}>
                        {word('service_categories.vehicle_class', v)}
                      </CheckRow>
                    ))}
                  </Cluster>
                  <SectionTitle level={3} size="sub">{t('svccat.editor.features')}</SectionTitle>
                  <Cluster gap={2} wrap>
                    {CATALOG_FEATURE_NAMES.map((f) => (
                      <CheckRow key={f} id={`svccat-feature-${f}`} checked={form.features[f] === true} onChange={(on) => setForm({ ...form, features: { ...form.features, [f]: on } })}>
                        {word('service_categories.category_feature', f)}
                      </CheckRow>
                    ))}
                  </Cluster>
                  {target !== undefined && target.scope !== 'market' && (
                    <>
                      <SectionTitle level={3} size="sub">{t('svccat.editor.locks')}</SectionTitle>
                      <Text as="p" role="caption" tone="tertiary" block>{t('svccat.editor.locks.hint')}</Text>
                      <Cluster gap={2} wrap>
                        {CATALOG_FEATURE_NAMES.map((f) => (
                          <CheckRow key={`lock-${f}`} id={`svccat-lock-${f}`} checked={form.lockedFeatures.includes(f)} onChange={() => setForm({ ...form, lockedFeatures: toggle(form.lockedFeatures, f) })}>
                            {word('service_categories.category_feature', f)}
                          </CheckRow>
                        ))}
                      </Cluster>
                    </>
                  )}
                  {draft !== undefined && !draft.ok && form.code !== '' && (
                    <Notice tone="warning">{draft.error.map((p) => t('svccat.problem', { code: reason(p.code), path: p.path })).join(' · ')}</Notice>
                  )}
                  <Text as="p" role="caption" tone="tertiary" block>{base !== undefined ? t('svccat.editor.base', { version: String(base.version) }) : t('svccat.editor.baseNone')}</Text>
                  <FormRow
                    columns="minmax(0, 1fr)"
                    action={
                      <Button variant="primary" disabled={busy || draft === undefined || !draft.ok || proposalReason.trim() === ''} onClick={() => void propose()}>{t('svccat.action.propose')}</Button>
                    }
                  >
                    <Field label={t('svccat.editor.reason')} htmlFor="svccat-proposal-reason">
                      <TextInput id="svccat-proposal-reason" value={proposalReason} maxLength={CATALOG_REASON_MAX} onChange={(e) => setProposalReason(e.target.value)} />
                    </Field>
                  </FormRow>

                  <SectionTitle level={3} size="sub">{t('svccat.copy.title')}</SectionTitle>
                  <Text as="p" role="caption" tone="tertiary" block>{t('svccat.copy.explain')}</Text>
                  {base === undefined || target === undefined ? (
                    <Text as="p" role="caption" tone="tertiary" block>{t('svccat.copy.needsVersion')}</Text>
                  ) : (
                    <>
                      <Cluster gap={2} wrap>
                        {copyChoices.map((m) => (
                          <CheckRow key={`copy-${m.id}`} id={`svccat-copy-${m.id}`} checked={copyTargets.includes(m.id)} onChange={() => setCopyTargets(toggle(copyTargets, m.id))}>
                            {m.name}
                          </CheckRow>
                        ))}
                        <CheckRow id="svccat-copy-fare" checked={copyFare} onChange={setCopyFare}>{t('svccat.copy.includeFare')}</CheckRow>
                      </Cluster>
                      <FormRow
                        columns="minmax(0, 1fr)"
                        action={
                          <Button variant="secondary" disabled={busy || copyTargets.length === 0 || copyReason.trim() === ''} onClick={() => void (async () => {
                            setBusy(true);
                            setMsg(undefined);
                            const res = await runtime.serviceCategories.copy({ source: { ...target, code: base.code }, targets: copyTargets.map((id) => ({ scope: 'market' as const, scopeRef: id })), includeFare: copyFare, reason: copyReason.trim() });
                            setBusy(false);
                            if (!res.ok) {
                              setMsg({ tone: 'danger', text: refusal(res.error) });
                              return;
                            }
                            const lines = res.value.results.map(copyResultSummary).map((r) => t(r.catalogOk && r.fareOk !== false ? 'svccat.copy.lineOk' : 'svccat.copy.lineRefused', { target: r.target === 'platform' || r.target === 'country' || r.target === 'region' ? word('policy.scope', r.target) : place.market(r.target), code: r.code !== undefined ? reason(r.code) : '' }));
                            setMsg({ tone: res.value.results.every((r) => r.catalog.ok && (r.fare === undefined || r.fare.ok)) ? 'success' : 'warning', text: lines.join(' · ') });
                            setCopyTargets([]);
                            setCopyReason('');
                            await load(marketId);
                          })()}>{t('svccat.action.copy')}</Button>
                        }
                      >
                        <Field label={t('svccat.copy.reason')} htmlFor="svccat-copy-reason">
                          <TextInput id="svccat-copy-reason" value={copyReason} maxLength={CATALOG_REASON_MAX} onChange={(e) => setCopyReason(e.target.value)} />
                        </Field>
                      </FormRow>
                    </>
                  )}
                </>
              )}
              </Stack>
            </Panel>
          </section>
        </>
      )}
    </>
  );
}
